import { HttpParams } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ColumnApi,
  DisplayedColumnsChangedEvent,
  FilterChangedEvent,
  GridReadyEvent,
} from 'ag-grid-community';
import {
  ColDef,
  DomLayoutType,
  GridApi,
  GridOptions,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-enterprise';
import moment from 'moment';
import { finalize, take } from 'rxjs';

import { FeatureToggleService } from 'app/shared/services/feature-toggle.service';
import {
  IAgGridFilterModelValue,
  ICustomFilterBuilderParams,
} from 'v2/modules/shared/interfaces/ag-grid/ag-grid.types';
import { IGetApiResponse } from 'v2/modules/shared/interfaces/api-response.interface';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';
import { LumResponseError } from 'v2/shared/api/lum-response-error';

import {
  COST_LAYERS_AG_GRID_COLUMN_DEFS,
  COST_LAYERS_AG_GRID_OPTIONS,
} from '../../config/cost-layers-basic-table.config';
import {
  CostLayersKeyByField,
  ECostLayerField,
  ECostLayerRelation,
} from '../../cost-layers.const';
import { ICostLayer } from '../../cost-layers.types';
import { CostLayersApiService } from '../../services/cost-layers-api.service';
import { CostLayersViewSettingsService } from '../../services/cost-layers-view-settings.service';

//

type _ColumnsViewSetting = 'costLayersColumns' | 'productCostLayersColumns';
type _FiltersViewSetting = 'costLayersFilters' | 'productCostLayersFilters';

const _getBackendKey = (key :string) =>
  CostLayersKeyByField[key as ECostLayerField] ?? key;

//

@Component({
  selector: 'lum-cost-layers-basic-table',
  templateUrl: './cost-layers-basic-table.component.html',
  styleUrls: ['./cost-layers-basic-table.component.scss'],
})
export class CostLayersBasicTableComponent implements OnInit, OnChanges {
  @Input() columnDefs :ColDef[];
  @Input() domLayout :DomLayoutType;
  @Input() gridOptions :GridOptions;
  @Input() pageSize :number;
  @Input() productId :number;
  @Input() hideEmptyCostLayers :boolean;

  /** Pass-through event from <ag-grid-angular> */
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() loadError = new EventEmitter<LumResponseError>();
  @Output() loadFinalize = new EventEmitter<void>();
  @Output() loadStart = new EventEmitter<{ params :HttpParams | HttpParamsHash }>();
  @Output() loadSuccess = new EventEmitter<IGetApiResponse<ICostLayer[]>>();

  #agGridApi :GridApi;
  #agColumnApi :ColumnApi;
  #viewSettingKeyColumns :_ColumnsViewSetting = 'costLayersColumns';
  #viewSettingKeyFilters :_FiltersViewSetting = 'costLayersFilters';

  constructor(
    private _feature :FeatureToggleService,
    private _costLayersApi :CostLayersApiService,
    private _viewSettings :CostLayersViewSettingsService,
  ) {}

  ngOnInit() {
    this.gridOptions = {
      ...COST_LAYERS_AG_GRID_OPTIONS,
      ...this.gridOptions,
    };

    if (!this.columnDefs) {
      this.columnDefs = [...COST_LAYERS_AG_GRID_COLUMN_DEFS];
    }

    if (!this._feature.isActive('utah_UTAH-183_single_cost_layer_view')) {
      const colDef = this.columnDefs.find(
        (columnDef :ColDef) => columnDef.field === ECostLayerField.DATE_CREATED,
      );

      if (colDef) {
        colDef.cellRenderer = null;
      }
    }
    if (!this._feature.isActive('utah-UTAH-271-cost_layer_serverside_multisort')) {
      this.columnDefs = this.columnDefs.map((columnDef :ColDef) => {
        if (columnDef.field === ECostLayerField.LOT_NUMBER) {
          columnDef.field = columnDef.colId = ECostLayerField.LOT_NUMBER_LEGACY;
        }
        if (columnDef.field === ECostLayerField.PRODUCT_SKU) {
          columnDef.field = columnDef.colId = ECostLayerField.PRODUCT;
        }
        if (columnDef.field === ECostLayerField.WAREHOUSE_NAME) {
          columnDef.field = columnDef.colId = ECostLayerField.WAREHOUSE_NAME_LEGACY;
        }

        return columnDef;
      });

      this.gridOptions.suppressMultiSort = true;
    }

    const _prefix = this.productId ? 'productCostLayers' : 'costLayers';
    this.#viewSettingKeyColumns = `${_prefix}Columns` as _ColumnsViewSetting;
    this.#viewSettingKeyFilters = `${_prefix}Filters` as _FiltersViewSetting;

    if (this.pageSize) {
      this.gridOptions.paginationPageSize = this.pageSize;
      this.gridOptions.cacheBlockSize = this.pageSize;
    }
  }
  ngOnChanges(changes :SimpleChanges) {
    if (changes['hideEmptyCostLayers']) {
      this.#agGridApi?.refreshServerSide({ purge: true });
    }
  }

  handleGridReady(e :GridReadyEvent) {
    this.gridReady.emit(e);

    this.setAgApis(e);

    this.#agGridApi.setServerSideDatasource(this.getServerSideDatasource());

    this.#updateGridFromViewSettings();
    this.#initSyncViewSettingsFromGrid();
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (rowParams :IServerSideGetRowsParams) => {
        const params = this.#buildQueryParams(rowParams);

        if (params['createdAt[eq]']) {
          const createdAtString :string = params['createdAt[eq]'];
          delete params['createdAt[eq]'];

          params['createdAt[gte]'] = moment(createdAtString)
            .startOf('day')
            .toISOString();
          params['createdAt[lte]'] = moment(createdAtString)
            .endOf('day')
            .toISOString();
        }

        this.loadStart.emit({ params });

        this._costLayersApi
          .get(params)
          .pipe(
            take(1),
            finalize(() => this.loadFinalize.emit()),
          )
          .subscribe({
            next: (response :IGetApiResponse<ICostLayer[]>) => {
              this.loadSuccess.emit(response);
              rowParams.success({
                rowData: response.data,
                rowCount: response.meta.total,
              });
            },
            error: (error :LumResponseError) => {
              this.loadError.emit(error);
              rowParams.fail();
            },
          });
      },
    };
  }

  // public to make testing easier
  setAgApis(e :GridReadyEvent) {
    this.#agGridApi = e.api;
    this.#agColumnApi = e.columnApi;
  }

  #buildQueryParams(params :IServerSideGetRowsParams) :HttpParams | HttpParamsHash {
    const pageSize = this.gridOptions?.paginationPageSize || 50;
    const queryParams = {
      ...AgGridUtils.mapQueryParams(
        {
          params,
          paginationPageSize: pageSize,
        },
        this._feature.isActive('utah-UTAH-271-cost_layer_serverside_multisort')
          ? {
              advancedFilters: true,
              defaultSortField: ECostLayerField.DATE_CREATED,
              getBackendKeyByColId: _getBackendKey,
              multiSort: true,
              customFilterBuilders: {
                [ECostLayerField.IS_VARIANCE]: (
                  params :ICustomFilterBuilderParams,
                ) => ({
                  field: params.field,
                  operator: 'eq',
                  value: `${params.data.type === 'variance'}`,
                }),
              },
            }
          : {},
      ),
      page: params?.request?.startRow ? params.request.startRow / pageSize + 1 : 1,
      relations: [
        ECostLayerRelation.LOT,
        ECostLayerRelation.PRODUCT,
        ECostLayerRelation.WAREHOUSE,
      ].join('|'),
    };

    // honor component inputs
    if (this.hideEmptyCostLayers) {
      queryParams.withRemainingQuantity = true;
    } else {
      delete queryParams.withRemainingQuantity;
    }

    if (this.productId) {
      queryParams.productId = this.productId;
    } else {
      delete queryParams.productId;
    }

    // filters
    const filters :{ [key :string] :string | undefined } = {};
    const filterEntries :[string, IAgGridFilterModelValue][] = Object.entries(
      params?.request?.filterModel ?? {},
    );

    if (filterEntries.length > 0) {
      for (const [key, value] of filterEntries) {
        if (
          this._feature.isActive('utah-UTAH-271-cost_layer_serverside_multisort')
        ) {
          if (key === 'isVariance') {
            queryParams[`${_getBackendKey(key)}[eq]`] = (
              value.type === 'variance'
            ).toString();
          }
        } else {
          filters[_getBackendKey(key)] = this.#transformFilterValueToPayload(
            key,
            value,
          );
        }
      }
    }

    return { ...queryParams, ...filters }; // remove ...filters when utah-UTAH-271-cost_layer_serverside_multisort is active
  }

  #initSyncViewSettingsFromGrid() {
    this.#agGridApi?.addEventListener(
      'displayedColumnsChanged',
      (e :DisplayedColumnsChangedEvent) => {
        this._viewSettings.updateState({
          [this.#viewSettingKeyColumns]: this.#agColumnApi?.getColumnState(),
        });
      },
    );

    this.#agGridApi?.addEventListener('filterChanged', (e :FilterChangedEvent) => {
      this._viewSettings.updateState({
        [this.#viewSettingKeyFilters]: this.#agGridApi?.getFilterModel(),
      });
    });
  }

  // remove with utah-UTAH-271-cost_layer_serverside_multisort
  #transformFilterValueToPayload(key :string, filterModel :any) :string {
    if (key === 'isVariance') {
      return filterModel.type === 'variance' ? 'true' : 'false';
    }

    if (['text', 'number'].includes(filterModel.filterType)) {
      return filterModel.filter || filterModel.type;
    }

    if (filterModel.filterType === 'set') {
      const values = filterModel.values;
      if (key) {
        return values.map((value :string) => key[value]).join(',');
      }

      return values.join(',');
    }

    if (filterModel.filterType === 'date') {
      return moment(filterModel.dateFrom).format('YYYY-MM-DD');
    }

    return '';
  }

  #updateGridFromViewSettings() {
    const columnState = this._viewSettings.get(this.#viewSettingKeyColumns);

    if (columnState) {
      this.#agColumnApi?.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }

    const filterModel = this._viewSettings.get(this.#viewSettingKeyFilters);

    if (filterModel) {
      this.#agGridApi?.setFilterModel(filterModel);
    }
  }
}
